import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import {
  catalogApi,
  type CreateCatalogRequest,
  type ReceiveJournalIssueResponse,
} from "@/lib/api/catalog";
import { useCatalogSettings } from "@/hooks/useCatalogSettings";
import type { ItemCopyData } from "./CopyDetailsEditor";
import { EMPTY_RECEIVE_ISSUE_FIELDS, ReceiveIssueFields } from "./ReceiveIssueFields";
import { ReceiveIssueCopySection } from "./ReceiveIssueCopySection";
import { ReceiveIssueSettingsState } from "./ReceiveIssueSettingsState";
import { getReceiveIssueValidationError } from "./receiveIssueValidation";

type ItemDefaults = CreateCatalogRequest["itemDefaults"];

interface ReceiveIssueFormProps {
  biblioId: string;
  onSuccess: (result: ReceiveJournalIssueResponse) => void;
  className?: string;
}

function optionalNumber(value: string) {
  return value ? Number(value) : undefined;
}

function errorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return "Unable to receive this issue. Please try again.";
}

export function ReceiveIssueForm({ biblioId, onSuccess, className }: ReceiveIssueFormProps) {
  const [fields, setFields] = useState(EMPTY_RECEIVE_ISSUE_FIELDS);
  const [copies, setCopies] = useState(1);
  const [copyData, setCopyData] = useState<ItemCopyData[]>([]);
  const [itemDefaults, setItemDefaults] = useState<ItemDefaults>({ statusCode: "AVAILABLE" });
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const {
    data: catalogSettings,
    isLoading: settingsLoading,
    isError: settingsError,
  } = useCatalogSettings();

  const submit = async () => {
    if (submittingRef.current || settingsLoading || settingsError || !catalogSettings) return;
    const invalid = getReceiveIssueValidationError(
      fields,
      copies,
      itemDefaults,
      copyData,
      catalogSettings.accessionNumberMode
    );
    if (invalid) {
      setMessage({ kind: "error", text: invalid });
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setMessage(null);
    try {
      const result = await catalogApi.receiveIssue(biblioId, {
        volumeNumber: fields.volumeNumber.trim(),
        volumePublicationYear: fields.volumePublicationYear.trim() || undefined,
        volumeOnly: fields.volumeOnly,
        issueNumberFrom: fields.volumeOnly ? undefined : optionalNumber(fields.issueNumberFrom),
        issueNumberTo: fields.volumeOnly ? undefined : optionalNumber(fields.issueNumberTo),
        issuePublicationYear: fields.issuePublicationYear.trim() || undefined,
        issuePublicationDate: fields.issuePublicationDate || undefined,
        copies,
        itemDefaults,
        itemCopies: copyData.slice(0, copies),
      });
      setMessage({ kind: "success", text: "Journal issue received successfully." });
      onSuccess(result);
    } catch (error) {
      setMessage({ kind: "error", text: errorMessage(error) });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (settingsLoading || settingsError || !catalogSettings) {
    return <ReceiveIssueSettingsState isLoading={settingsLoading} className={className} />;
  }

  return (
    <div className={className}>
      <Card className="mb-6 space-y-4 p-6">
        <h2 className="text-lg font-semibold">Issue details</h2>
        <ReceiveIssueFields values={fields} onChange={setFields} />
        {message ? (
          <p
            role={message.kind === "error" ? "alert" : "status"}
            className={
              message.kind === "error" ? "text-sm text-destructive" : "text-sm text-green-700"
            }
          >
            {message.text}
          </p>
        ) : null}
      </Card>
      <ReceiveIssueCopySection
        copies={copies}
        onCopiesChange={setCopies}
        copyData={copyData}
        onCopyDataChange={setCopyData}
        itemDefaults={itemDefaults}
        onItemDefaultsChange={setItemDefaults}
        onSubmit={submit}
        loading={isSubmitting}
        catalogSettings={catalogSettings}
      />
    </div>
  );
}
